import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Tabs as TabsNav } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"

import { GamesUpdateStatusWidget } from "features/game-management/GamesUpdateStatusWidget"
import { GeoSelector } from "features/game-management/GeoSelector"
import { formatMs } from "features/game-management/helpers/formatDates"
import { CategoriesTab } from "features/game-management/tab-contents/CategoriesTab"
import { HomepageTab } from "features/game-management/tab-contents/HomepageTab"
import { LibraryTab } from "features/game-management/tab-contents/LibraryTab"
import { ProvidersTab } from "features/game-management/tab-contents/ProvidersTab"
import { SectionsTab } from "features/game-management/tab-contents/SectionsTab"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { PERMISSIONS } from "constants/permissions"

import { gameService } from "services/game/game.service"
import { UpdateStatus } from "services/game/type"

type UpdateButtonVariant = "updating" | "cooldown" | "adminOverride" | "ready"

enum Tabs {
  LIBRARY = "library",
  SECTIONS = "sections",
  HOMEPAGE = "homepage",
  CATEGORIES = "categories",
  PROVIDERS = "providers"
}

const TAB_ORDER: Tabs[] = [Tabs.LIBRARY, Tabs.SECTIONS, Tabs.HOMEPAGE, Tabs.CATEGORIES, Tabs.PROVIDERS]

const TAB_LABELS: Record<Tabs, string> = {
  [Tabs.LIBRARY]: "Library",
  [Tabs.SECTIONS]: "Sections",
  [Tabs.HOMEPAGE]: "Homepage",
  [Tabs.CATEGORIES]: "Categories",
  [Tabs.PROVIDERS]: "Providers"
}

// Tabs that read/write per-GEO state. Others ignore the GEO selector.
const GEO_AWARE_TABS = new Set<Tabs>([Tabs.SECTIONS, Tabs.HOMEPAGE, Tabs.CATEGORIES])

export const GameManagement = () => {
  const { darkMode } = useProps()
  const { has } = usePermissions()
  const { t } = useTranslation("translation", { useSuspense: false })

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.LIBRARY)
  const [geo, setGeo] = useState<string>("*")

  const [status, setStatus] = useState<UpdateStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [triggering, setTriggering] = useState(false)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const loadStatus = async () => {
    setLoadingStatus(true)
    const s = await gameService.getUpdateStatus()
    setStatus(s ?? null)
    setLoadingStatus(false)
  }

  useEffect(() => {
    loadStatus()
  }, [])

  const cooldownLeftMs = useMemo(() => {
    const next = status?.nextAllowedAt ? new Date(status.nextAllowedAt).getTime() : 0
    return next ? next - now : 0
  }, [status?.nextAllowedAt, now])

  const isAdmin = has(PERMISSIONS.GAME_UPDATE_BYPASS_COOLDOWN)

  const disabled = loadingStatus || triggering || !!status?.inProgress || (!status?.allowed && !isAdmin)

  const updateBtn: { variant: UpdateButtonVariant; title: string; label: string } = useMemo(() => {
    const inProgress = !!status?.inProgress || triggering
    const allowed = !!status?.allowed
    const hasNext = !!status?.nextAllowedAt
    const isCooldownBlocked = !allowed && hasNext && !isAdmin
    const isAdminOverride = isAdmin && !allowed

    if (inProgress) {
      return { variant: "updating", title: t("gameManagement.updateRunning"), label: t("gameManagement.updating") }
    }
    if (isCooldownBlocked) {
      const inStr = formatMs(cooldownLeftMs)
      return {
        variant: "cooldown",
        title: t("gameManagement.availableIn", { time: inStr }),
        label: t("gameManagement.updateIn", { time: inStr })
      }
    }
    if (isAdminOverride) {
      return {
        variant: "adminOverride",
        title: t("gameManagement.triggerAdminOverride"),
        label: t("gameManagement.updateGamesAdmin")
      }
    }
    return { variant: "ready", title: t("gameManagement.triggerUpdate"), label: t("gameManagement.updateGamesNow") }
  }, [status?.inProgress, status?.allowed, status?.nextAllowedAt, triggering, isAdmin, cooldownLeftMs])

  const onTriggerUpdate = async () => {
    if (disabled) return

    setTriggering(true)
    const res = await gameService.triggerUpdate()
    setTriggering(false)

    if (!res) return

    if (res.ok) toast.success("Games update started")
    else {
      if (res.inProgress) toast.info("Update is already running")
      if (res.nextAllowedAt) toast.info(`Available after ${new Date(res.nextAllowedAt).toLocaleString()}`)
    }

    await loadStatus()
  }

  const tabContent: Record<Tabs, JSX.Element> = {
    [Tabs.LIBRARY]: <LibraryTab />,
    [Tabs.SECTIONS]: <SectionsTab geo={geo} />,
    [Tabs.HOMEPAGE]: <HomepageTab geo={geo} />,
    [Tabs.CATEGORIES]: <CategoriesTab geo={geo} />,
    [Tabs.PROVIDERS]: <ProvidersTab />
  }

  const showGeoSelector = GEO_AWARE_TABS.has(activeTab)
  const geoSelectorScope = activeTab === Tabs.HOMEPAGE ? { kind: "category_order" as const } : { kind: "game_order" as const }

  return (
    <div className={clsx(darkMode && "dark")}>
      <div className="w-full max-w-[100vw] p-4 pt-0 sm:p-8 sm:pt-0">
        <TopBar title={t("pages.gameManagement.title")} />

        <GamesUpdateStatusWidget status={status} loading={loadingStatus} now={now} cooldownLeftMs={cooldownLeftMs} onRefresh={loadStatus} />

        <div className="w-full flex flex-col gap-6 py-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsNav
              variant="pills"
              activeTab={activeTab}
              onChange={(key) => setActiveTab(key as Tabs)}
              tabs={TAB_ORDER.map((tab) => ({
                key: tab,
                label: t(`gameManagement.tabs.${tab}`, { defaultValue: TAB_LABELS[tab] })
              }))}
            />

            <button
              type="button"
              className={clsx(
                "btn btn-outline btn-sm",
                "border-[rgba(132,_0,_168,_0.8)] text-[rgba(132,_0,_168,_1)] hover:bg-[rgba(132,_0,_168,_0.08)]",
                "dark:text-white dark:border-[rgba(132,_0,_168,_0.8)]",
                disabled && "btn-disabled opacity-90"
              )}
              onClick={onTriggerUpdate}
              disabled={disabled}
              title={updateBtn.title}
            >
              {updateBtn.label}
            </button>
          </div>

          {showGeoSelector && (
            <div className="flex items-center justify-end -mb-2">
              <GeoSelector value={geo} onChange={setGeo} scope={geoSelectorScope} />
            </div>
          )}

          <div className="rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A] p-4">
            {tabContent[activeTab]}
          </div>
        </div>
      </div>
    </div>
  )
}
